import { useForm } from "@mantine/form"
import {
  CameraPlus,
  CaretDown,
  CheckCircle,
  Plus,
  User,
  WarningCircle,
} from "@phosphor-icons/react"
import {
  Button,
  Checkbox,
  FormGroup,
  FormInput,
  Image,
  Modal,
  Note,
  RadioGroup,
  SpinnerOverlay,
  Tabs,
  Tag,
  TextArea,
  Toast,
} from "@vesatogo/grass-core"
import { FileUpload } from "@vesatogo/grass-dropzone"
import { inr } from "@vesatogo/utils"
import classNames from "classnames"
import dayjs from "dayjs"
import { pick } from "lodash-es"
import { useEffect, useMemo, useState } from "react"
import toast from "react-hot-toast"
import { useSearchParams } from "react-router-dom"
import { GstTaxRow } from "~/components/GstBreakdownCard"
import {
  Order_By,
  useAddUpdateIssuanceNotesMutation,
  useInvoiceIssuanceNotesQuery,
  useRecordTradeInvoiceReturnMutation,
  useTradeIssuanceNotesQuery,
} from "~/generated/graphql"
import { calculateFinalAmount } from "~/pages/pavti/lib/calculate-bifurcation-total"
import {
  Allocation,
  DistributeRevisedQuantityModal,
} from "./DistributeRevisedQuantityModal"

// ─── Types ────────────────────────────────────────────────────────────────────

export type NoteModalType = "CREDIT" | "DEBIT"
type PaymentKind = "amount_percent" | "rate" | "flat"
type NoteMode = "Revise Rate" | "Revise Quantity"

interface IssuanceNoteModalProps {
  type: NoteModalType
  onSubmit?: () => void
}

interface FormValues {
  noteMode: NoteMode
  paymentKind: PaymentKind
  inputValue: string
  reason: string
  attachments: any[]
  allocations: Allocation[]
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PARAM_KEY: Record<NoteModalType, string> = {
  CREDIT: "creditNote",
  DEBIT: "debitNote",
}

const PAYMENT_KINDS: { value: PaymentKind; label: string }[] = [
  { value: "amount_percent", label: "Percent" },
  { value: "rate", label: "Rate" },
  { value: "flat", label: "Flat Amount" },
]

const getItemNoteAmount = (
  tradeItem: any,
  adjustment: number,
  paymentKind: PaymentKind
) => {
  if (!adjustment || adjustment <= 0) return 0
  const qty = Number(tradeItem?.quantity) || 0
  const itemRate = Number(tradeItem?.rate) || 0
  switch (paymentKind) {
    case "amount_percent":
      return (itemRate * qty * adjustment) / 100
    case "flat":
      return adjustment
    case "rate":
    default:
      return adjustment * qty
  }
}

// ─── Small reusable pieces ────────────────────────────────────────────────────

/** A single labelled info cell, e.g. "Supplier: John" */
const InfoCell = ({
  label,
  value,
}: {
  label: string
  value?: React.ReactNode
}) => (
  <div>
    <span className="font-600">{label}: </span>
    {value ?? "--"}
  </div>
)

/** A row in the amount summary footer */
const SummaryRow = ({
  label,
  hint,
  amount,
  className = "",
  hidden = false,
}: {
  label: string
  hint?: string
  amount: number
  className?: string
  hidden?: boolean
}) => (
  <div
    className={classNames(
      "flex items-center justify-between gap-2 text-sm",
      hidden ? "hidden" : ""
    )}
  >
    <span className="flex-1 font-500">
      {label}
      {hint ? (
        <span className="text-gray-500 italic font-400 text-xs ml-1">
          {hint}
        </span>
      ) : null}
    </span>
    <span className={`shrink-0 tabular-nums ${className}`}>{inr(amount)}</span>
  </div>
)

type VarietyTaxEntry = {
  name: string
  igst: { amount: number; pct: number }
  cgst: { amount: number; pct: number }
  sgst: { amount: number; pct: number }
}

const VarietyGstSection = ({
  varieties,
  totalGst,
  amountClassName = "text-red-600",
  defaultOpen = true,
}: {
  varieties: VarietyTaxEntry[]
  totalGst: number
  amountClassName?: string
  defaultOpen?: boolean
}) => {
  const [open, setOpen] = useState(defaultOpen)
  if (!varieties.length && totalGst <= 0) return null

  return (
    <div className="space-y-2">
      <button
        type="button"
        className="w-full flex items-center justify-between gap-2 text-sm"
        onClick={() => setOpen(prev => !prev)}
      >
        <span className="flex items-center gap-1 font-500">
          <CaretDown
            size={14}
            className={classNames(
              "transition-transform",
              open ? "" : "-rotate-90"
            )}
          />
          Variety GST:
        </span>
        <span className={`shrink-0 tabular-nums font-600 ${amountClassName}`}>
          {inr(totalGst)}
        </span>
      </button>
      {open && varieties.length > 0 && (
        <div className="space-y-2 pl-1">
          {varieties.map(variety => {
            const varietyTotal =
              variety.igst.amount + variety.cgst.amount + variety.sgst.amount
            return (
              <div key={variety.name} className="space-y-1">
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span className="font-600 text-gray-800">{variety.name}</span>
                  <span
                    className={`shrink-0 tabular-nums font-600 ${amountClassName}`}
                  >
                    {inr(varietyTotal)}
                  </span>
                </div>
                <div className="grid grid-cols-3 text-xs text-gray-600 border border-gray-300 rounded-md bg-white overflow-hidden">
                  <div className="px-2 py-1.5 border-r border-gray-300">
                    IGST ({variety.igst.pct}%) {inr(variety.igst.amount)}
                  </div>
                  <div className="px-2 py-1.5 border-r border-gray-300">
                    CGST ({variety.cgst.pct}%) {inr(variety.cgst.amount)}
                  </div>
                  <div className="px-2 py-1.5">
                    SGST ({variety.sgst.pct}%) {inr(variety.sgst.amount)}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const getRevisedRate = (
  currentRate: number,
  adjustment: number,
  kind: string,
  isReduction: boolean
) => {
  if (!adjustment || Number.isNaN(adjustment)) return currentRate
  if (kind === "amount_percent") {
    const delta = (currentRate * adjustment) / 100
    return isReduction ? currentRate - delta : currentRate + delta
  }
  if (kind === "flat" || kind === "quantity") return currentRate
  return isReduction ? currentRate - adjustment : currentRate + adjustment
}

const kindLabel = (kind?: string) => {
  if (kind === "quantity") return "Revise Quantity"
  if (kind === "amount_percent") return "Percentage"
  if (kind === "flat") return "Flat Amount"
  return "Revise Rate"
}

/** 3-column row used in the debit summary grid */
const SummaryGridRow = ({
  label,
  qty,
  qtyUnit,
  amount,
  className = "",
}: {
  label: string
  qty?: number
  qtyUnit: string
  amount: number
  className?: string
}) => (
  <div
    className={`flex items-center justify-between gap-2 text-sm ${className}`}
  >
    <span className="flex-1 min-w-0 font-500">{label}</span>
    {qty != null && qty > 0 && (
      <span className="shrink-0 text-xs bg-gray-100 text-gray-500 rounded px-1.5 py-0.5 tabular-nums">
        {qty}&nbsp;{qtyUnit}
      </span>
    )}
    <span className="shrink-0 tabular-nums font-500">{inr(amount)}</span>
  </div>
)

// ─── Main Component ───────────────────────────────────────────────────────────

export const IssuanceNoteModal = ({
  type,
  onSubmit,
}: IssuanceNoteModalProps) => {
  const isCredit = type === "CREDIT"
  const paramKey = PARAM_KEY[type]

  // ── URL state ──────────────────────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams()
  const noteId = Number(searchParams.get(paramKey))
  const isOpen =
    searchParams.get(paramKey) === "new" ||
    searchParams.get(paramKey) === "view"
  const tradeId = Number(searchParams.get("tradeId"))
  const invoiceId = Number(searchParams.get("invoiceId"))
  const isCreditNote = searchParams.get("user_role") === "buyer"
  const userRole = searchParams.get("user_role")

  // Label and colour always follow the note type (Credit = green, Debit = red).
  // What differs per role is only the financial direction (math), not the label.
  const displayAsCredit = isCredit
  const noteLabel = isCredit ? "Credit" : "Debit"

  // Direction of rate/amount revision depends on role x type
  // buyer credit|supplier debit -> reduction ; buyer debit|supplier credit -> increase
  const isReduction = isCredit === (userRole === "buyer")

  const onClose = () => {
    ;[paramKey, "user_role", "tradeId", "invoiceId"].forEach(k =>
      searchParams.delete(k)
    )
    setSearchParams(searchParams)
  }

  // ── Form state ────────────────────────────────────────────────────────────
  const form = useForm<FormValues>({
    initialValues: {
      noteMode: "Revise Rate",
      paymentKind: "rate",
      inputValue: "",
      reason: "",
      attachments: [],
      allocations: [],
    },
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDistributeModalOpen, setIsDistributeModalOpen] = useState(false)
  const [distributeMode, setDistributeMode] = useState<"quantity" | "rate">(
    "quantity"
  )

  const rateAdjustmentLabel =
    form.values.paymentKind === "amount_percent"
      ? isReduction
        ? "Percentage Reduction"
        : "Percentage Increase"
      : form.values.paymentKind === "flat"
      ? isReduction
        ? "Flat Amount Reduction"
        : "Flat Amount Increase"
      : isReduction
      ? "Rate Reduction"
      : "Rate Increase"

  // ── Query ──────────────────────────────────────────────────────────────────
  const [{ data, fetching: tradeFetching }, refetch] =
    useTradeIssuanceNotesQuery({
      variables: {
        tradeId,
        where: {
          type: { _eq: type },
          user_role: { _eq: userRole },
          _or: [
            {
              is_verified: {
                _is_null: true,
              },
            },
            {
              is_verified: {
                _neq: false,
              },
            },
          ],
        },
        orderBy: { created_at: Order_By.DescNullsLast },
      },
      pause: !isOpen || !tradeId || !userRole,
      requestPolicy: "cache-and-network",
    })

  const [{ data: invData, fetching: invoiceFetching }, invoiceRefetch] =
    useInvoiceIssuanceNotesQuery({
      variables: {
        invoiceId,
        where: { type: { _eq: type }, user_role: { _eq: userRole } },
        orderBy: { created_at: Order_By.DescNullsLast },
      },
      pause: !isOpen || !invoiceId || !userRole,
      requestPolicy: "cache-and-network",
    })

  const trade = data?.trade_by_pk
  const invoice = invData?.invoice_by_pk
  const isViewMode = searchParams.get(paramKey) === "view"
  const notes = trade?.issuance_notes || invoice?.issuance_notes || []
  const isNoteAdded = Boolean(
    invoice?.issuance_notes_aggregate?.aggregate?.count
  )

  const sellerPayable = trade?.seller_bifurcation?.total
  // calculateFinalAmount(
  //   pick(
  //     trade?.seller_bifurcation as any,
  //     "deducted_amount",
  //     "seller_expense_amount",
  //     "seller_expense_tax_amount",
  //     "seller_tax_amount",
  //     "supplier_charge"
  //   )
  // ) || 0
  const buyerPayable = trade
    ? trade?.buyer_bifurcation?.total || 0
    : invoice?.buyer_bifurcation?.total || 0

  const payableBase =
    userRole === "buyer" ? (buyerPayable as number) : (sellerPayable as number)

  const [, addUpdateNote] = useAddUpdateIssuanceNotesMutation()
  const [, recordReturnTrade] = useRecordTradeInvoiceReturnMutation()

  const pickTax = (taxes: any[], name: string) =>
    taxes?.find((t: any) => t.tax_name === name)

  const tradeTaxes =
    trade?.trade_items?.[0]?.taxes ??
    invoice?.trades?.[0]?.trade_items?.[0]?.taxes ??
    trade?.taxes ??
    invoice?.trades?.[0]?.taxes ??
    []
  const tradeIgstPct = pickTax(tradeTaxes, "IGST")?.percentage ?? 0
  const tradeCgstPct = pickTax(tradeTaxes, "CGST")?.percentage ?? 0
  const tradeSgstPct = pickTax(tradeTaxes, "SGST")?.percentage ?? 0

  useEffect(() => {
    if (isOpen) {
      form.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // ── Derived trade values ───────────────────────────────────────────────────
  const invoiceAmount = trade
    ? calculateFinalAmount(
        pick(trade?.seller_bifurcation as any, "deducted_amount")
      )
    : calculateFinalAmount(
        pick(invoice?.buyer_bifurcation as any, "deducted_amount")
      ) ?? 0

  const quantity =
    trade?.trade_items?.[0]?.quantity ??
    invoice?.trades?.reduce(
      (acc, trade) => acc + trade.trade_items?.[0]?.quantity,
      0
    ) ??
    0

  const rate = trade
    ? trade?.trade_items?.[0]?.rate
    : invoice
    ? invoice?.total_amount / quantity
    : 0
  const qtyUnit =
    trade?.trade_items?.[0]?.quantity_unit?.name ??
    invoice?.trades?.[0]?.trade_items?.[0]?.quantity_unit?.name ??
    undefined

  const allTradeItems = useMemo(() => {
    if (trade?.trade_items) return trade.trade_items
    if (invoice?.trades)
      return invoice.trades.flatMap((t: any) => t.trade_items ?? [])
    return []
  }, [trade, invoice])

  const rateDisplay = useMemo(
    () =>
      allTradeItems
        .filter((item: any) => item.rate != null)
        .map(
          (item: any) =>
            `${inr(item.rate)}/${
              item.quantity_unit?.name || qtyUnit || "Units"
            }`
        )
        .join(" | ") || `${inr(rate)}/${qtyUnit || "Units"}`,
    [allTradeItems, rate, qtyUnit]
  )

  const quantityDisplay = useMemo(
    () =>
      allTradeItems
        .filter((item: any) => item.quantity != null)
        .reduce((acc, item: any) => acc + item?.quantity, 0) + " Units" ||
      `${quantity} ${qtyUnit || "Units"}`,
    [allTradeItems, quantity, qtyUnit]
  )

  // For buyers the form UI is flipped:
  // buyer Credit → debit-style form (Revise Rate / Revise Quantity)
  // buyer Debit  → credit-style form (Percent / Rate / Flat Amount)
  const showDebitUI = userRole === "buyer" ? isCredit : !isCredit
  const isReviseQty = showDebitUI && form.values.noteMode === "Revise Quantity"

  // ── Allocation-based GST for Revise Qty ────────────────────────────────
  const allocationGst = useMemo(() => {
    if (!isReviseQty || !form.values.allocations?.length) return null

    const igst = { amount: 0, pct: 0 }
    const cgst = { amount: 0, pct: 0 }
    const sgst = { amount: 0, pct: 0 }
    let totalNoteAmount = 0

    for (const alloc of form.values.allocations) {
      const tradeItem = allTradeItems.find(
        (item: any) => item.id === alloc.tradeItemId
      )
      if (!tradeItem) continue

      const allocAmount = alloc.quantity * (Number(tradeItem.rate) || 0)
      totalNoteAmount += allocAmount

      const taxes = (tradeItem as any).taxes || []
      for (const tax of taxes) {
        const taxAmount = allocAmount * (Number(tax.percentage) / 100)
        if (tax.tax_name === "IGST") igst.amount += taxAmount
        else if (tax.tax_name === "CGST") cgst.amount += taxAmount
        else if (tax.tax_name === "SGST") sgst.amount += taxAmount
      }
    }

    if (totalNoteAmount > 0) {
      igst.pct = (igst.amount / totalNoteAmount) * 100
      cgst.pct = (cgst.amount / totalNoteAmount) * 100
      sgst.pct = (sgst.amount / totalNoteAmount) * 100
    }

    return { igst, cgst, sgst, totalNoteAmount }
  }, [isReviseQty, form.values.allocations, allTradeItems])

  const varietyTaxBreakdown = useMemo(() => {
    if (!form.values.allocations?.length) return []

    const varietyMap = new Map<
      string,
      {
        name: string
        igst: { amount: number; pct: number }
        cgst: { amount: number; pct: number }
        sgst: { amount: number; pct: number }
      }
    >()

    for (const alloc of form.values.allocations) {
      const tradeItem = allTradeItems.find(
        (item: any) => item.id === alloc.tradeItemId
      )
      if (!tradeItem) continue

      const varietyName =
        tradeItem.commodity_variety?.name ||
        tradeItem.commodity?.name ||
        "Unknown"

      if (!varietyMap.has(varietyName)) {
        varietyMap.set(varietyName, {
          name: varietyName,
          igst: { amount: 0, pct: 0 },
          cgst: { amount: 0, pct: 0 },
          sgst: { amount: 0, pct: 0 },
        })
      }

      const entry = varietyMap.get(varietyName)!
      const allocAmount = isReviseQty
        ? alloc.quantity * (Number(tradeItem.rate) || 0)
        : getItemNoteAmount(tradeItem, alloc.quantity, form.values.paymentKind)

      const taxes = tradeItem.taxes || []
      for (const tax of taxes) {
        const taxAmount = allocAmount * (Number(tax.percentage) / 100)
        if (tax.tax_name === "IGST") {
          entry.igst.amount += taxAmount
          entry.igst.pct = Number(tax.percentage)
        } else if (tax.tax_name === "CGST") {
          entry.cgst.amount += taxAmount
          entry.cgst.pct = Number(tax.percentage)
        } else if (tax.tax_name === "SGST") {
          entry.sgst.amount += taxAmount
          entry.sgst.pct = Number(tax.percentage)
        }
      }
    }

    return Array.from(varietyMap.values())
  }, [
    isReviseQty,
    form.values.allocations,
    form.values.paymentKind,
    allTradeItems,
  ])

  const varietyGstTotals = useMemo(() => {
    if (!varietyTaxBreakdown.length) return null
    return varietyTaxBreakdown.reduce(
      (acc, variety) => ({
        igst: acc.igst + variety.igst.amount,
        cgst: acc.cgst + variety.cgst.amount,
        sgst: acc.sgst + variety.sgst.amount,
      }),
      { igst: 0, cgst: 0, sgst: 0 }
    )
  }, [varietyTaxBreakdown])

  // ── Calculate note amount from any string value ────────────────────────────
  const calcAmount = (value: string): number => {
    const val = parseFloat(value)
    if (isNaN(val) || val < 0) return 0
    if (isReviseQty) return val * rate
    switch (form.values.paymentKind) {
      case "amount_percent":
        return (invoiceAmount * val) / 100
      case "rate":
        return quantity * val
      case "flat":
        return val
    }
  }

  const rateAllocationNoteAmount = useMemo(() => {
    if (isReviseQty || !form.values.allocations?.length) return null
    return form.values.allocations.reduce((acc, alloc) => {
      const tradeItem = allTradeItems.find(
        (item: any) => item.id === alloc.tradeItemId
      )
      if (!tradeItem) return acc
      return (
        acc +
        getItemNoteAmount(tradeItem, alloc.quantity, form.values.paymentKind)
      )
    }, 0)
  }, [
    isReviseQty,
    form.values.allocations,
    form.values.paymentKind,
    allTradeItems,
  ])

  const rateAllocationSummary = useMemo(() => {
    if (isReviseQty || !form.values.allocations?.length) return []

    const trades: any[] = trade ? [trade] : ((invoice?.trades || []) as any[])
    const supplierMap = new Map<
      string,
      {
        key: string
        name: string
        total: number
        commodities: {
          key: string
          name: string
          grades: {
            name: string
            quantity: number
            unit: string
            rate: number
            amount: number
          }[]
        }[]
      }
    >()

    for (const alloc of form.values.allocations) {
      const parentTrade = trades.find((t: any) =>
        (t?.trade_items || []).some(
          (item: any) => item.id === alloc.tradeItemId
        )
      )
      const tradeItem =
        (parentTrade?.trade_items || []).find(
          (item: any) => item.id === alloc.tradeItemId
        ) || allTradeItems.find((item: any) => item.id === alloc.tradeItemId)
      if (!tradeItem) continue

      const amount = getItemNoteAmount(
        tradeItem,
        alloc.quantity,
        form.values.paymentKind
      )
      if (amount <= 0) continue

      const sKey = `${parentTrade?.id ?? "unknown"}:${
        parentTrade?.seller?.id ?? parentTrade?.seller?.name ?? "unknown"
      }`
      let supplier = supplierMap.get(sKey)
      if (!supplier) {
        supplier = {
          key: sKey,
          name: parentTrade?.seller?.name || "--",
          total: 0,
          commodities: [],
        }
        supplierMap.set(sKey, supplier)
      }

      const variety =
        tradeItem.commodity_variety ?? parentTrade?.commodity_variety
      const commodity = tradeItem.commodity
      const cKey = String(
        variety?.id ??
          commodity?.id ??
          variety?.name ??
          commodity?.name ??
          "commodity"
      )
      const commodityName = variety?.name || commodity?.name || "Commodity"
      let commodityEntry = supplier.commodities.find(c => c.key === cKey)
      if (!commodityEntry) {
        commodityEntry = { key: cKey, name: commodityName, grades: [] }
        supplier.commodities.push(commodityEntry)
      }

      commodityEntry.grades.push({
        name: tradeItem.variety_grade?.name || "Grade",
        quantity: alloc.quantity,
        unit: tradeItem.quantity_unit?.name || "kg",
        rate: Number(tradeItem.rate) || 0,
        amount,
      })
      supplier.total += amount
    }

    return Array.from(supplierMap.values()).filter(s => s.total > 0)
  }, [
    isReviseQty,
    form.values.allocations,
    form.values.paymentKind,
    allTradeItems,
    trade,
    invoice,
  ])

  const noteAmount =
    allocationGst?.totalNoteAmount ??
    rateAllocationNoteAmount ??
    calcAmount(form.values.inputValue)
  const qtyReduced = isReviseQty ? parseFloat(form.values.inputValue) || 0 : 0
  const revisedQty = quantity - qtyReduced

  const addToInvoice = userRole === "buyer" ? !isCredit : isCredit

  const effectiveIgstPct = allocationGst?.igst.pct ?? tradeIgstPct
  const effectiveCgstPct = allocationGst?.cgst.pct ?? tradeCgstPct
  const effectiveSgstPct = allocationGst?.sgst.pct ?? tradeSgstPct

  const effectiveIgstAmount =
    allocationGst?.igst.amount ??
    varietyGstTotals?.igst ??
    noteAmount * (tradeIgstPct / 100)
  const effectiveCgstAmount =
    allocationGst?.cgst.amount ??
    varietyGstTotals?.cgst ??
    noteAmount * (tradeCgstPct / 100)
  const effectiveSgstAmount =
    allocationGst?.sgst.amount ??
    varietyGstTotals?.sgst ??
    noteAmount * (tradeSgstPct / 100)

  const noteAmountWithGst =
    noteAmount + effectiveIgstAmount + effectiveCgstAmount + effectiveSgstAmount
  const revisedAmount = addToInvoice
    ? payableBase + noteAmountWithGst
    : payableBase - noteAmountWithGst

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (isSubmitting || noteAmount <= 0 || !form.values.reason.trim()) return
    setIsSubmitting(true)
    try {
      if (isReviseQty && form.values.allocations?.length) {
        const { error } = await recordReturnTrade({
          items: form.values.allocations.map(allocation => ({
            trade_item_id: allocation.tradeItemId as any,
            returned_quantity: allocation.quantity,
          })),
        })
        if (error) {
          toast.custom(<Toast title="Something went wrong!" intent="danger" />)
        } else {
          toast.custom(<Toast title="Note added successfully!" />)
        }
      }

      const { error } = await addUpdateNote({
        input: {
          remarks: form.values.reason,
          type,
          user_role: String(userRole),
          trade_id: tradeId ? String(tradeId) : undefined,
          invoice_id: invoiceId ? String(invoiceId) : undefined,
          kind: isReviseQty ? "quantity" : form.values.paymentKind,
          input_value: 0,
          items: form.values.allocations?.length
            ? form.values.allocations.map(allocation => ({
                trade_item_id: String(allocation.tradeItemId),
                input_value: allocation.quantity,
              }))
            : undefined,
          attachments: form.values.attachments?.map(
            attachment => attachment.id
          ),
        },
      })

      if (error) {
        toast.custom(
          <Toast
            title={"Something went wrong!" + error?.message}
            intent="danger"
          />,
          {
            duration: 5000,
          }
        )
      }
      //  else if (isReviseQty && form.values.allocations?.length) {
      //   const { error } = await recordReturnTrade({
      //     items: form.values.allocations.map(allocation => ({
      //       trade_item_id: allocation.tradeItemId as any,
      //       returned_quantity: allocation.quantity,
      //     })),
      //   })
      //   if (error) {
      //     toast.custom(<Toast title="Something went wrong!" intent="danger" />)
      //   } else {
      //     toast.custom(<Toast title="Note added successfully!" />)
      //   }
      // }
      else {
        toast.custom(<Toast title="Note added successfully!" />)
      }

      if (tradeId) {
        refetch({
          requestPolicy: "network-only",
        })
      }
      if (invoiceId) {
        invoiceRefetch({
          requestPolicy: "network-only",
        })
      }
      onSubmit?.()
      onClose()
    } catch (e) {
      console.error(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Shared trade info grid ─────────────────────────────────────────────────
  const renderTradeInfo = () => (
    <div className="grid grid-cols-2 gap-2">
      {trade?.seller?.name && (
        <InfoCell label="Supplier" value={trade?.seller?.name} />
      )}
      <InfoCell
        label="Buyer"
        value={trade?.buyer?.name || invoice?.buyer?.name}
      />
      <div className="col-span-2">
        <InfoCell label="Rate" value={rateDisplay} />
      </div>
      <div className="col-span-2">
        <InfoCell label="Quantity" value={quantityDisplay} />
      </div>
      <div className="col-span-2">
        <InfoCell
          label={`Invoice Amount ${
            userRole === "buyer" ? "(Buyer)" : "(Seller)"
          }`}
          value={inr(invoiceAmount)}
        />
      </div>
    </div>
  )

  // ── Create mode ────────────────────────────────────────────────────────────
  const renderCreateMode = (disableQtyRevise = false) => (
    <div className="bg-white border border-gray-200 rounded py-4 px-2 text-sm">
      <SpinnerOverlay show={tradeFetching || invoiceFetching} />
      <div className="flex flex-col gap-3">
        {renderTradeInfo()}

        <Note
          intent="info"
          className="!text-sm text-blue-600"
          title={
            (
              <div className="flex gap-2">
                <WarningCircle size={20} />
                {showDebitUI
                  ? "Adjustment can be applied by reducing the rate or by returning a portion of the quantity."
                  : "Any rate raise is applied on the original invoice rate, which will raise the total amount."}
              </div>
            ) as any
          }
        />

        {/* Debit only — note mode selector */}
        {showDebitUI && (
          <RadioGroup
            label="Type"
            value={form.values.noteMode}
            onChange={val => {
              form.setFieldValue("noteMode", val as NoteMode)
              form.setFieldValue("inputValue", "")
              form.setFieldValue("allocations", [])
            }}
            className="flex gap-4"
            required
          >
            <Checkbox type="radio" value="Revise Rate" label="Revise Rate" />
            <Checkbox
              disabled={userRole === "seller" || disableQtyRevise}
              type="radio"
              value="Revise Quantity"
              label="Revise Quantity"
            />
          </RadioGroup>
        )}

        {/* Payment kind + amount input (credit always, debit only on Revise Rate) */}
        {(!showDebitUI || form.values.noteMode === "Revise Rate") && (
          <>
            <div className="flex justify-between">
              <RadioGroup
                label="Rate Adjustment Method"
                value={form.values.paymentKind}
                onChange={val => {
                  form.setFieldValue("paymentKind", val as PaymentKind)
                  form.setFieldValue("inputValue", "")
                  form.setFieldValue("allocations", [])
                }}
                className="flex gap-4"
                required
              >
                {PAYMENT_KINDS.map(({ value, label }) => (
                  <Checkbox
                    key={value}
                    type="radio"
                    value={value}
                    label={label}
                  />
                ))}
              </RadioGroup>
              <Button
                variant="minimal"
                className={"mt-6"}
                text={` ${
                  form.values.paymentKind === "amount_percent"
                    ? "Percentage"
                    : form.values.paymentKind === "flat"
                    ? "Flat Amount"
                    : "Rate"
                }`}
                leftIcon={<Plus />}
                size="sm"
                onClick={() => {
                  setDistributeMode("rate")
                  setIsDistributeModalOpen(true)
                }}
              />
            </div>
            <div className="text-gray-600 italic">
              {form.values.paymentKind === "rate"
                ? `${
                    isReduction ? "Reduce" : "Increase"
                  } the current rate by a fixed ₹/kg amount.`
                : form.values.paymentKind === "amount_percent"
                ? `${
                    isReduction ? "Reduce" : "Increase"
                  } the total value of the selected grade by a fixed percentage.`
                : `${
                    isReduction ? "Reduce" : "Increase"
                  } the total value of the selected grade by a fixed amount.`}
            </div>

            {rateAllocationSummary.length > 0 && (
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="divide-y divide-gray-200">
                  {rateAllocationSummary.map(supplier => (
                    <div key={supplier.key} className="p-3 space-y-2">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="shrink-0 h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                            <User size={16} />
                          </div>
                          <div className="font-600 text-sm truncate">
                            {supplier.name}
                          </div>
                        </div>
                        <div className="shrink-0 text-sm font-600 text-gray-800">
                          {noteLabel} Note: {inr(supplier.total)}
                        </div>
                      </div>
                      <ol className="list-decimal pl-5 space-y-2 text-sm text-gray-700">
                        {supplier.commodities.map(commodity => (
                          <li key={commodity.key}>
                            <div className="font-500 text-gray-800">
                              {commodity.name}
                            </div>
                            <div className="text-gray-700 text-xs font-600 mt-2">
                              {commodity.grades.map((g, idx) => {
                                return (
                                  <>
                                    • {" " + g.name + ": "}{" "}
                                    <span className="!font-400">
                                      {`${g.quantity} ${g.unit} | ${inr(
                                        g.rate
                                      )}/${g.unit}`}
                                    </span>
                                    <br />
                                  </>
                                )
                              })}
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  ))}
                </div>
                <div className="bg-gray-50 border-t border-gray-200 px-3 py-2 flex items-center justify-between gap-3 text-sm">
                  <span className="text-gray-600">
                    Selected Suppliers: {rateAllocationSummary.length}
                  </span>
                  <span className="font-700 text-gray-800">
                    Total{" "}
                    {form.values.paymentKind === "amount_percent"
                      ? "Percentage"
                      : form.values.paymentKind === "flat"
                      ? "Flat Amount"
                      : "Rate"}{" "}
                    {noteLabel} Note: {inr(noteAmount)}
                  </span>
                </div>
              </div>
            )}
          </>
        )}

        {/* Debit only — Revise Quantity input */}
        {isReviseQty && (
          <FormGroup label="Quantity to Reduce" required>
            <div className="relative flex gap-1 justify-between">
              <FormInput
                // type="number"
                disabled
                rightElement={
                  <div className="mr-1 text-gray-500 text-sm">Units</div>
                }
                className={"w-fit"}
                value={form.values.inputValue}
                onChange={e => form.setFieldValue("inputValue", e)}
                placeholder="0"
                helperText={`Revised Quantity: ${revisedQty} Units`}
              />
              <Button
                variant="minimal"
                className={"!w-fit"}
                text={"Quantity"}
                leftIcon={<Plus />}
                onClick={() => {
                  setDistributeMode("quantity")
                  setIsDistributeModalOpen(true)
                }}
              />
            </div>
          </FormGroup>
        )}

        <FormGroup label="Reason" required>
          <TextArea
            placeholder="Reason"
            value={form.values.reason}
            onChange={(e: any) => form.setFieldValue("reason", e.target.value)}
            className="w-full"
          />
        </FormGroup>

        <FormGroup label="Attachments">
          <FileUpload
            multiple
            method="put"
            appName="setutrade"
            environment={import.meta.env.VITE_APP_ENV}
            endpoint={`https://${
              import.meta.env.VITE_REST_URL
            }/rest/trade/uploader/`}
            onChange={e => {
              return
            }}
            className="grid-cols-3"
            uploadPlaceHolder=""
            uploadImage={
              <div className="px-5 pt-5 pb-3 ">
                <CameraPlus size={30} className="text-green" />
              </div>
            }
            media={form.values.attachments || []}
            onDelete={file => {
              const indexOfFile = form.values.attachments?.findIndex(
                attachment => attachment.url === file.url
              )
              if (indexOfFile !== -1) {
                form.removeListItem("attachments", indexOfFile)
              }
            }}
            onSuccess={media => {
              const newAttachments = media
                .map(m => m?.data?.media)
                .filter(Boolean)
              form.setFieldValue("attachments", [
                ...(form.values.attachments || []),
                ...newAttachments,
              ])
            }}
            onError={message => {
              toast.error(message)
            }}
          />
        </FormGroup>
      </div>

      {/* Summary footer */}
      <div className="mt-4 bg-gray-50 rounded p-2 space-y-2 text-base">
        {!showDebitUI ? (
          <>
            <SummaryRow
              label={`Existing ${
                userRole === "buyer" ? "Buyer" : "Supplier"
              } Payable (incl. expenses & GST):`}
              amount={payableBase}
              className="font-600"
            />
            <SummaryRow
              label={`${noteLabel} Note Amount (excl. GST):`}
              amount={noteAmount}
              className={`font-600 ${
                displayAsCredit ? "text-green-600" : "text-red-600"
              }`}
            />
            <SummaryRow
              label={`${noteLabel} Note Amount (incl. GST):`}
              amount={noteAmountWithGst}
              className={`font-600 ${
                displayAsCredit ? "text-green-600" : "text-red-600"
              }`}
            />

            {/* Live GST breakdown */}
            {varietyTaxBreakdown.length > 0 ? (
              <div className="space-y-3 border border-slate-300 rounded-lg p-3">
                {varietyTaxBreakdown.map(variety => (
                  <div key={variety.name} className="space-y-1">
                    <div className="font-600 text-sm text-gray-800">
                      {variety.name}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                      <span>
                        IGST ({variety.igst.pct}%) {inr(variety.igst.amount)}
                      </span>
                      <span>
                        CGST ({variety.cgst.pct}%) {inr(variety.cgst.amount)}
                      </span>
                      <span>
                        SGST ({variety.sgst.pct}%) {inr(variety.sgst.amount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              (effectiveIgstPct > 0 ||
                effectiveCgstPct > 0 ||
                effectiveSgstPct > 0) && (
                <div className="flex w-full py-3 items-center justify-center gap-3 border border-slate-300 rounded-lg">
                  <GstTaxRow
                    name="IGST"
                    amount={effectiveIgstAmount}
                    pct={effectiveIgstPct}
                    field={"igst_percentage"}
                    taxType="igst"
                    autoCalculated={true}
                    onPctChange={() => {}}
                  />
                  <GstTaxRow
                    name="CGST"
                    amount={effectiveCgstAmount}
                    pct={effectiveCgstPct}
                    field={"cgst_percentage"}
                    taxType="cgst"
                    autoCalculated={true}
                    onPctChange={() => {}}
                  />
                  <GstTaxRow
                    name="SGST"
                    amount={effectiveSgstAmount}
                    pct={effectiveSgstPct}
                    field={"sgst_percentage"}
                    taxType="sgst"
                    autoCalculated={true}
                    onPctChange={() => {}}
                  />
                </div>
              )
            )}

            <hr className="border-gray-300" />
            <SummaryRow
              label="Revise Payable Amount:"
              amount={revisedAmount}
              className="font-700 text-green-700"
            />
          </>
        ) : (
          <>
            <SummaryGridRow
              label={`Existing ${
                userRole === "buyer" ? "Buyer" : "Supplier"
              } Payable (incl. expenses & GST):`}
              qty={quantity}
              qtyUnit={qtyUnit as any}
              amount={payableBase}
              className="font-600 text-gray-600"
            />
            <SummaryGridRow
              label={`${noteLabel} Note Amount (excl. GST):`}
              qty={0}
              qtyUnit={qtyUnit as any}
              amount={noteAmount}
              className={`font-600 ${
                displayAsCredit ? "text-green-600" : "text-red-600"
              }`}
            />
            <SummaryGridRow
              label={`${noteLabel} Note Amount (incl. GST):`}
              qty={0}
              qtyUnit={qtyUnit as any}
              amount={noteAmountWithGst}
              className="font-600 text-red-600"
            />

            {varietyTaxBreakdown.length > 0 ? (
              <div className="space-y-3 border border-gray-300 rounded-lg p-3">
                {varietyTaxBreakdown.map(variety => (
                  <div key={variety.name} className="space-y-1">
                    <div className="flex justify-between font-600 text-sm text-gray-800">
                      {variety.name}{" "}
                      <span className="text-red-600 font-600">
                        {inr(
                          variety.igst.amount +
                            variety.cgst.amount +
                            variety.sgst.amount
                        )}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-sm text-gray-600 bg-white border-1 border-gray-300 rounded-lg p-1">
                      <div className="border-r-1 border-gray-300">
                        IGST ({variety.igst.pct}%){" "}
                        <span className="text-green-400 font-700">
                          {" "}
                          {inr(variety.igst.amount)}
                        </span>
                      </div>
                      <div className="border-r-1 border-gray-300">
                        CGST ({variety.cgst.pct}%){" "}
                        <span className="text-green-400 font-700">
                          {inr(variety.cgst.amount)}
                        </span>
                      </div>
                      <div>
                        SGST ({variety.sgst.pct}%){" "}
                        <span className="text-green-400 font-700">
                          {inr(variety.sgst.amount)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              (effectiveIgstPct > 0 ||
                effectiveCgstPct > 0 ||
                effectiveSgstPct > 0) && (
                <div className="flex gap-2 w-full py-3 items-center justify-center border border-slate-300 rounded-lg">
                  <GstTaxRow
                    name="IGST"
                    amount={effectiveIgstAmount}
                    pct={effectiveIgstPct}
                    field={"igst_percentage"}
                    taxType="igst"
                    autoCalculated={true}
                    onPctChange={() => {}}
                  />
                  <GstTaxRow
                    name="CGST"
                    amount={effectiveCgstAmount}
                    pct={effectiveCgstPct}
                    field={"cgst_percentage"}
                    taxType="cgst"
                    autoCalculated={true}
                    onPctChange={() => {}}
                  />
                  <GstTaxRow
                    name="SGST"
                    amount={effectiveSgstAmount}
                    pct={effectiveSgstPct}
                    field={"sgst_percentage"}
                    taxType="sgst"
                    autoCalculated={true}
                    onPctChange={() => {}}
                  />
                </div>
              )
            )}

            <div className="border-t border-gray-200 pt-2">
              <SummaryGridRow
                label="Revise Payable:"
                qty={revisedQty}
                qtyUnit={qtyUnit as any}
                amount={revisedAmount}
                className="font-700 text-green-500"
              />
            </div>
          </>
        )}
      </div>
    </div>
  )

  // ── View mode ──────────────────────────────────────────────────────────────
  const renderViewMode = (note: any, currentActiveNoteId: any) => {
    if (!note) return null
    const isVerified = note.is_verified === true
    const tradesForView: any[] = trade
      ? [trade]
      : ((invoice?.trades || []) as any[])
    const noteItems = note.items || []

    const resolveTradeItem = (noteItem: any) => {
      const itemId = noteItem.trade_item?.id ?? noteItem.trade_item_id
      const fromNote = noteItem.trade_item
      const fromTrade =
        allTradeItems.find((item: any) => item.id === itemId) || {}
      const parentTrade = tradesForView.find((t: any) =>
        (t?.trade_items || []).some((item: any) => item.id === itemId)
      )
      return {
        id: itemId,
        ...fromTrade,
        ...fromNote,
        parentTrade,
      }
    }

    const changeGroups = (() => {
      if (!noteItems.length) return []
      const supplierMap = new Map<
        string,
        {
          key: string
          name: string
          tradeIds: number[]
          commodities: {
            key: string
            name: string
            grades: {
              id: number
              name: string
              rate: number
              revisedRate: number
              unit: string
              quantity: number
              originalQuantity: number
              revisedQuantity: number
              noteAmount: number
              gstAmount: number
              exclGstAmount: number
            }[]
          }[]
        }
      >()

      for (const noteItem of noteItems) {
        const tradeItem = resolveTradeItem(noteItem)
        const parentTrade = tradeItem.parentTrade
        const tradeIdValue = parentTrade?.id ?? trade?.id
        const sKey = `${tradeIdValue ?? "unknown"}:${
          parentTrade?.seller?.id ??
          trade?.seller?.id ??
          parentTrade?.seller?.name ??
          trade?.seller?.name ??
          "unknown"
        }`
        let supplier = supplierMap.get(sKey)
        if (!supplier) {
          supplier = {
            key: sKey,
            name: parentTrade?.seller?.name || trade?.seller?.name || "--",
            tradeIds: [],
            commodities: [],
          }
          supplierMap.set(sKey, supplier)
        }
        if (tradeIdValue != null && !supplier.tradeIds.includes(tradeIdValue)) {
          supplier.tradeIds.push(tradeIdValue)
        }

        const variety = tradeItem.commodity_variety
        const commodity = tradeItem.commodity
        const varietyName = variety?.name || commodity?.name || "Commodity"
        const commodityName =
          commodity?.name && commodity?.name !== varietyName
            ? commodity.name
            : undefined
        const displayName = commodityName
          ? `${commodityName} - ${varietyName}`
          : varietyName
        const cKey = String(
          variety?.id ?? commodity?.id ?? varietyName ?? "commodity"
        )
        let commodityEntry = supplier.commodities.find(c => c.key === cKey)
        if (!commodityEntry) {
          commodityEntry = { key: cKey, name: displayName, grades: [] }
          supplier.commodities.push(commodityEntry)
        }

        const currentRate = Number(tradeItem.rate) || 0
        const adjustment = Number(noteItem.input_value) || 0
        const unit = tradeItem.quantity_unit?.name || "kg"
        const noteItemAmount = Number(noteItem.amount) || 0
        const gstAmount = (noteItem.taxes ?? []).reduce(
          (sum, tax: any) => sum + (Number(tax.amount) || 0),
          0
        )
        commodityEntry.grades.push({
          id: noteItem.id,
          name: tradeItem.variety_grade?.name || "Grade",
          rate: currentRate,
          revisedRate: getRevisedRate(
            currentRate,
            adjustment,
            note.kind,
            isReduction
          ),
          unit,
          quantity: Number(noteItem.input_value) || 0,
          originalQuantity: Number(tradeItem.quantity) || 0,
          revisedQuantity: Math.max(
            (Number(tradeItem.quantity) || 0) -
              (Number(noteItem.input_value) || 0),
            0
          ),
          noteAmount: noteItemAmount,
          gstAmount,
          exclGstAmount: Math.max(noteItemAmount - gstAmount, 0),
        })
      }

      return Array.from(supplierMap.values())
    })()

    const viewVarietyTaxes = (() => {
      if (!noteItems.length) return [] as VarietyTaxEntry[]
      const varietyMap = new Map<string, VarietyTaxEntry>()

      for (const noteItem of noteItems) {
        const tradeItem = resolveTradeItem(noteItem)
        const varietyName =
          tradeItem.commodity_variety?.name ||
          tradeItem.commodity?.name ||
          "Unknown"
        if (!varietyMap.has(varietyName)) {
          varietyMap.set(varietyName, {
            name: varietyName,
            igst: { amount: 0, pct: 0 },
            cgst: { amount: 0, pct: 0 },
            sgst: { amount: 0, pct: 0 },
          })
        }
        const entry = varietyMap.get(varietyName)!
        const noteItemGross = Number(noteItem.amount) || 0
        const itemTaxes = noteItem.taxes?.length
          ? noteItem.taxes
          : (tradeItem.taxes || []).map((tax: any) => ({
              tax_name: tax.tax_name,
              percentage: tax.percentage,
              amount:
                noteItemGross *
                (Number(tax.percentage) / (100 + Number(tax.percentage))),
            }))

        for (const tax of itemTaxes) {
          const taxAmount = Number(tax.amount) || 0
          const pct = Number(tax.percentage) || 0
          if (tax.tax_name === "IGST") {
            entry.igst.amount += taxAmount
            entry.igst.pct = pct
          } else if (tax.tax_name === "CGST") {
            entry.cgst.amount += taxAmount
            entry.cgst.pct = pct
          } else if (tax.tax_name === "SGST") {
            entry.sgst.amount += taxAmount
            entry.sgst.pct = pct
          }
        }
      }

      return Array.from(varietyMap.values())
    })()

    const viewVarietyGstTotal = viewVarietyTaxes.reduce(
      (acc, v) => acc + v.igst.amount + v.cgst.amount + v.sgst.amount,
      0
    )
    const noteTaxes = note.taxes ?? []
    const noteIgstAmount = Number(pickTax(noteTaxes, "IGST")?.amount ?? 0)
    const noteCgstAmount = Number(pickTax(noteTaxes, "CGST")?.amount ?? 0)
    const noteSgstAmount = Number(pickTax(noteTaxes, "SGST")?.amount ?? 0)
    const noteTotalGst = noteIgstAmount + noteCgstAmount + noteSgstAmount
    const displayGstTotal =
      viewVarietyGstTotal > 0
        ? viewVarietyGstTotal
        : noteTotalGst ||
          noteItems.reduce(
            (sum, ni: any) =>
              sum +
              (ni.taxes ?? []).reduce(
                (s, t: any) => s + (Number(t.amount) || 0),
                0
              ),
            0
          )
    const noteGrossAmount =
      noteItems.reduce((sum, ni: any) => sum + (Number(ni.amount) || 0), 0) ||
      Number(note.amount ?? 0)
    const noteExclGst = Math.max(noteGrossAmount - displayGstTotal, 0)

    const preNotePayable = isVerified
      ? addToInvoice
        ? payableBase - noteGrossAmount
        : payableBase + noteGrossAmount
      : payableBase

    const savedRevised = isVerified
      ? payableBase
      : addToInvoice
      ? payableBase + noteGrossAmount
      : payableBase - noteGrossAmount
    const amountColor = addToInvoice ? "text-green-600" : "text-red-600"

    const status =
      note?.is_verified === true
        ? "Approved"
        : note?.is_verified === false
        ? "Rejected"
        : "Pending"
    const changeSectionTitle =
      note.kind === "quantity"
        ? "Revise Quantity Information"
        : "Revise Rate Information"

    return (
      <div className="space-y-4 bg-white p-2 rounded-lg mt-4">
        <SpinnerOverlay show={tradeFetching || invoiceFetching} />
        <div className="bg-[#F0FDF4] border border-[#B9F8CF] text-[#00A63E] p-3 rounded-xl flex gap-2 items-start text-sm">
          <CheckCircle size={24} className="shrink-0 mt-0.5" />
          <div>
            <div>{noteLabel} Note Created Successfully.</div>
            <div>
              {tradeId ? "Trade #" : "Invoice #"}
              {tradeId || invoiceId} has been updated with the{" "}
              {noteLabel.toLowerCase()} note.
            </div>
          </div>
        </div>

        <div>
          <div className="flex justify-between">
            <h3 className="font-500 underline mb-2">
              {invoiceId ? "Invoice Information" : "Trade Information"}
            </h3>
            <Tag
              intent={
                status === "Approved"
                  ? "primary"
                  : status === "Pending"
                  ? "info"
                  : "danger"
              }
            >
              {status}
            </Tag>
          </div>
          <div className="text-sm space-y-1">
            <InfoCell
              label="Date"
              value={
                trade?.created_at
                  ? dayjs(trade.created_at).format("D MMM 'YY")
                  : invoice?.created_at
                  ? dayjs(invoice.created_at).format("D MMM 'YY")
                  : "--"
              }
            />
            <div className="grid grid-cols-2 gap-2">
              <InfoCell
                label="Buyer"
                value={trade?.buyer?.name || invoice?.buyer?.name}
              />
              <InfoCell label="Quantity" value={quantityDisplay} />
            </div>
            <InfoCell label="Invoice Amount" value={inr(invoiceAmount)} />
          </div>
        </div>

        <div>
          <h3 className="font-500 underline mb-2">
            {noteLabel} Note Information
          </h3>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <InfoCell
              label="Date"
              value={dayjs(note.created_at).format("D MMM 'YY")}
            />
            <InfoCell label="Type" value={kindLabel(note.kind)} />
          </div>
        </div>

        {changeGroups.length > 0 && (
          <div>
            <h3 className="font-500 underline mb-2 flex gap-2">
              {changeSectionTitle}{" "}
              <span>
                <Tag
                  variant="outline"
                  intent="warning"
                  className="!rounded-md text-xs"
                >
                  # {note.id}
                </Tag>
              </span>
            </h3>
            <div className="space-y-3">
              {changeGroups.map(supplier => {
                const subtotal = supplier.commodities.reduce(
                  (sum, commodity) =>
                    sum +
                    commodity.grades.reduce(
                      (s, grade) => s + grade.noteAmount,
                      0
                    ),
                  0
                )
                return (
                  <div
                    key={supplier.key}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 flex items-center justify-between gap-3">
                      <div className="font-500 text-gray-700 text-sm flex gap-2">
                        <Tag
                          intent="info"
                          variant="solid"
                          className="font-600 !bg-blue-500 !rounded-md"
                        >
                          Trade ID:{" "}
                          {supplier.tradeIds.map(id => `#${id}`).join(", ")}
                        </Tag>
                        {supplier.name}
                      </div>
                      <Tag
                        intent="none"
                        variant="outline"
                        className="font-400 text-sm !rounded-md"
                      >
                        Subtotal {noteLabel}:{" "}
                        <span className={classNames(amountColor, "font-700")}>
                          {inr(subtotal)}
                        </span>
                      </Tag>
                    </div>
                    <ol className="list-decimal pl-5 py-2 pr-3 space-y-3">
                      {supplier.commodities.map(commodity => (
                        <li key={commodity.key}>
                          <div className="font-600 text-gray-800 text-sm">
                            Variety:{" "}
                            <span className="!font-400"> {commodity.name}</span>
                          </div>
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-gray-200 border-b border-gray-200 text-xs text-gray-700">
                                <th className="text-left font-500 px-3 py-2">
                                  Grade
                                </th>
                                {note.kind === "quantity" ? (
                                  <>
                                    <th className="text-left font-500 px-3 py-2">
                                      Original Qty
                                    </th>
                                    <th className="text-left font-500 px-3 py-2">
                                      Revised Qty
                                    </th>
                                  </>
                                ) : (
                                  <>
                                    <th className="text-left font-500 px-3 py-2">
                                      Original Rate
                                    </th>
                                    <th className="text-left font-500 px-3 py-2">
                                      Revised Rate
                                    </th>
                                  </>
                                )}
                                <th className="text-right font-500 px-3 py-2">
                                  {noteLabel} Note (incl. GST)
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {commodity.grades.map(grade => (
                                <tr
                                  key={grade.id}
                                  className="border-b border-gray-100"
                                >
                                  <td className="px-3 py-2 text-gray-800 whitespace-nowrap">
                                    {grade.name}
                                  </td>
                                  {note.kind === "quantity" ? (
                                    <>
                                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                        {grade.originalQuantity} {grade.unit}
                                      </td>
                                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                        {grade.revisedQuantity} {grade.unit}
                                      </td>
                                    </>
                                  ) : (
                                    <>
                                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                        {inr(grade.rate)}/{grade.unit}
                                      </td>
                                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                                        {inr(grade.revisedRate)}/{grade.unit}
                                      </td>
                                    </>
                                  )}
                                  <td className="px-3 py-2 text-right font-600 text-gray-800 whitespace-nowrap">
                                    {inr(grade.noteAmount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </li>
                      ))}
                    </ol>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        <div>
          <h3 className="font-600 underline mb-2">Attachment</h3>
          <div className="flex gap-3">
            {note.attachments?.length ? (
              note.attachments?.map((attachment: any) => (
                <div
                  key={attachment.id}
                  className="w-14 h-14 bg-[#3BB0E5] rounded-lg shadow-md flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => window.open(attachment.media.url, "_blank")}
                >
                  <Image
                    src={attachment.media.url}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
              ))
            ) : (
              <div className="text-sm pl-5">No attachments!</div>
            )}
          </div>
        </div>

        <div className="bg-gray-100 p-3 rounded-lg text-sm space-y-2">
          <SummaryRow
            label="Existing Invoice:"
            amount={preNotePayable}
            className="font-600"
          />
          <SummaryRow
            label={`${noteLabel} Note`}
            hint="(Exclude GST)"
            amount={noteExclGst}
            className={`font-600 ${amountColor}`}
          />
          <VarietyGstSection
            varieties={viewVarietyTaxes}
            totalGst={displayGstTotal}
            amountClassName={amountColor}
          />
          <div className="border-t border-gray-200 pt-2">
            <SummaryRow
              label="Sub-total"
              hint={`(${noteLabel} note + GST)`}
              amount={noteGrossAmount || noteExclGst + displayGstTotal}
              className={`font-600 ${amountColor}`}
            />
          </div>
          <div className="border-t border-gray-200 pt-2">
            <SummaryRow
              label={
                userRole === "buyer"
                  ? "Revise Receivable Amount"
                  : "Revise Payable Amount"
              }
              hint={
                addToInvoice
                  ? "(Existing Invoice + Sub-total)"
                  : "(Existing Invoice - Sub-total)"
              }
              amount={savedRevised}
              className="font-700 text-green-700"
            />
          </div>
        </div>
      </div>
    )
  }

  const currentActiveNoteId = notes?.find(
    note => note?.is_verified === true
  )?.id
  // ── Modal shell ────────────────────────────────────────────────────────────
  return (
    <>
      <Modal
        isOpen={Boolean(isOpen) && !isDistributeModalOpen}
        onClose={onClose}
        title={`${noteLabel} Note:${tradeId ? "Trade #" : "Invoice #"}${
          trade?.id || invoice?.id
        }`}
        bodyClassName="max-w-lg bg-gray-200 !text-gray-800"
        contentClassName="!m-0 !p-3"
        primaryActionButtonProps={
          !isViewMode
            ? {
                text: "Confirm",
                onClick: handleSubmit,
                loading: isSubmitting,
                disabled: !form.values.reason || noteAmount <= 0,
              }
            : undefined
        }
        secondaryActionButtonProps={{
          text: "Cancel",
          onClick: onClose,
          disabled: isSubmitting,
        }}
      >
        <div className="flex flex-col h-full max-h-[70vh]">
          <div className=" overflow-y-auto flex-1 space-y-4">
            {isViewMode ? (
              tradeFetching || invoiceFetching ? (
                <SpinnerOverlay show={true} />
              ) : notes.length > 0 ? (
                <Tabs
                  tabs={notes.map((note: any, index: number) => ({
                    id: String(note.id || index),
                    header: dayjs(note.created_at).format("D MMM 'YY"),
                    panel: renderViewMode(note, currentActiveNoteId),
                  }))}
                />
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No notes found
                </div>
              )
            ) : (
              renderCreateMode(isNoteAdded)
            )}
          </div>
        </div>
      </Modal>

      {isDistributeModalOpen && (
        <DistributeRevisedQuantityModal
          isOpen={isDistributeModalOpen}
          onClose={() => setIsDistributeModalOpen(false)}
          trades={trade ? [trade] : invoice?.trades || []}
          initialAllocations={form.values.allocations}
          mode={distributeMode}
          paymentKind={form.values.paymentKind}
          adjustmentLabel={rateAdjustmentLabel}
          isReduction={isReduction}
          noteLabel={noteLabel}
          onSave={(allocs, totalQty) => {
            form.setFieldValue("allocations", allocs)
            form.setFieldValue("inputValue", String(totalQty))
            setIsDistributeModalOpen(false)
          }}
        />
      )}
    </>
  )
}
